import type {
  CreateReportRequest,
  ExecuteReportResponse,
  ExecuteStageResponse,
  ListReportsParams,
  ListReportsResponse,
  ReportConfig,
  UpdateReportRequest
} from '../../types/platform-report'

import { istioMeshApiClient } from '../api/istioMeshApiClient'
import { logger } from '../../utils/logger'

// API base path for platform reports
const API_BASE_PATH = '/platform/reports/v1'

// Helper type for API responses that might have different structures
interface ApiResponse<T> {
  data?: T
  [key: string]: unknown
}

export const platformReportsService = {
  /**
   * List all report configurations with filtering, sorting, and pagination
   */
  async listReports(params?: ListReportsParams): Promise<ListReportsResponse> {
    try {
      logger.log('Fetching platform reports with params:', params)

      // Build query parameters, filtering out undefined values
      const queryParams: Record<string, string> = {}
      if (params?.search) queryParams.search = params.search
      if (params?.status) queryParams.status = params.status
      if (params?.sourceType) queryParams.sourceType = params.sourceType
      if (params?.sinkType) queryParams.sinkType = params.sinkType
      if (params?.sortBy) queryParams.sortBy = params.sortBy
      if (params?.sortOrder) queryParams.sortOrder = params.sortOrder
      if (params?.page) queryParams.page = params.page.toString()
      if (params?.limit) queryParams.limit = params.limit.toString()

      const response = await istioMeshApiClient.get<
        ApiResponse<ListReportsResponse> | ListReportsResponse
      >(API_BASE_PATH, queryParams)

      logger.log('Platform reports API response:', response)

      // Handle different response structures - API might return data directly or wrapped
      const responseData = (response as ApiResponse<ListReportsResponse>).data || response
      const typedResponse = responseData as ListReportsResponse

      const reports = typedResponse.reports || []
      const total = typedResponse.total ?? reports.length
      const page = typedResponse.page ?? params?.page ?? 1
      const limit = typedResponse.limit ?? params?.limit ?? 20
      const totalPages = typedResponse.totalPages ?? Math.ceil(total / limit)

      return {
        reports: Array.isArray(reports) ? reports : [],
        total,
        page,
        limit,
        totalPages
      }
    } catch (error) {
      logger.error('Failed to fetch platform reports:', error)
      throw new Error('Failed to fetch platform reports. Please try again later.')
    }
  },

  /**
   * Get a single report configuration by ID
   */
  async getReportById(id: string): Promise<ReportConfig> {
    try {
      logger.log('Fetching platform report by ID:', id)

      const response = await istioMeshApiClient.get<ApiResponse<ReportConfig> | ReportConfig>(
        `${API_BASE_PATH}/${id}`
      )

      logger.log('Platform report API response:', response)

      // Handle response that might be wrapped in data property
      const report = (response as ApiResponse<ReportConfig>).data || (response as ReportConfig)

      if (!report || !report.id) {
        throw new Error(`Report with ID ${id} not found`)
      }

      return report
    } catch (error) {
      logger.error('Failed to fetch platform report:', error)
      if (error instanceof Error && error.message.includes('not found')) {
        throw error
      }
      throw new Error('Failed to fetch platform report. Please try again later.')
    }
  },

  /**
   * Create a new report configuration
   */
  async createReport(data: CreateReportRequest): Promise<ReportConfig> {
    try {
      logger.log('Creating platform report:', data)

      const response = await istioMeshApiClient.post<ApiResponse<ReportConfig> | ReportConfig>(
        API_BASE_PATH,
        data
      )

      logger.log('Platform report created:', response)

      // Handle response that might be wrapped in data property
      const report = (response as ApiResponse<ReportConfig>).data || (response as ReportConfig)

      return report
    } catch (error) {
      logger.error('Failed to create platform report:', error)
      throw new Error('Failed to create platform report. Please try again later.')
    }
  },

  /**
   * Update an existing report configuration
   */
  async updateReport(id: string, data: UpdateReportRequest): Promise<ReportConfig> {
    try {
      logger.log('Updating platform report:', id, data)

      const response = await istioMeshApiClient.put<ApiResponse<ReportConfig> | ReportConfig>(
        `${API_BASE_PATH}/${id}`,
        data
      )

      logger.log('Platform report updated:', response)

      // Handle response that might be wrapped in data property
      const report = (response as ApiResponse<ReportConfig>).data || (response as ReportConfig)

      return report
    } catch (error) {
      logger.error('Failed to update platform report:', error)
      throw new Error('Failed to update platform report. Please try again later.')
    }
  },

  /**
   * Delete a report configuration
   */
  async deleteReport(id: string): Promise<void> {
    try {
      logger.log('Deleting platform report:', id)

      await istioMeshApiClient.delete(`${API_BASE_PATH}/${id}`)

      logger.log('Platform report deleted:', id)
    } catch (error) {
      logger.error('Failed to delete platform report:', error)
      throw new Error('Failed to delete platform report. Please try again later.')
    }
  },

  /**
   * Execute a report by ID (trigger manual run)
   */
  async executeReport(id: string): Promise<ExecuteReportResponse> {
    try {
      logger.log('Executing platform report:', id)

      const response = await istioMeshApiClient.post<
        ApiResponse<ExecuteReportResponse> | ExecuteReportResponse
      >(`${API_BASE_PATH}/execute/${id}`)

      logger.log('Platform report executed:', response)

      // Handle response that might be wrapped in data property
      const result =
        (response as ApiResponse<ExecuteReportResponse>).data || (response as ExecuteReportResponse)

      return {
        success: result.success ?? true,
        message: result.message ?? 'Report executed successfully'
      }
    } catch (error) {
      logger.error('Failed to execute platform report:', error)
      throw new Error('Failed to execute platform report. Please try again later.')
    }
  },

  /**
   * Execute a report by ID to a specific stage (source or transform) for preview
   */
  async executeReportStage(
    id: string,
    stage: 'source' | 'transform'
  ): Promise<ExecuteStageResponse> {
    try {
      logger.log('Executing platform report stage:', id, stage)

      const response = await istioMeshApiClient.post<
        ApiResponse<ExecuteStageResponse> | ExecuteStageResponse
      >(`${API_BASE_PATH}/execute/${id}`, { stage })

      logger.log('Platform report stage executed:', response)

      // Handle response that might be wrapped in data property or returned directly
      // The API returns: { code, success, stage, data, message, traceId }
      const result = response as ExecuteStageResponse

      // If the response has the expected structure, return it
      if ('stage' in result && 'data' in result) {
        return result
      }

      // If wrapped in data property (from istioMeshApiClient unwrapping)
      const wrappedResponse = response as ApiResponse<ExecuteStageResponse>
      if (wrappedResponse.data && 'stage' in wrappedResponse.data) {
        return wrappedResponse.data
      }

      // Return as-is and let the UI handle it
      return result
    } catch (error) {
      logger.error('Failed to execute platform report stage:', error)
      throw new Error(`Failed to execute report ${stage} stage. Please try again later.`)
    }
  },

  /**
   * Schedule a report run by ID
   */
  async scheduleRun(id: string): Promise<ExecuteReportResponse> {
    try {
      logger.log('Scheduling platform report run:', id)

      const response = await istioMeshApiClient.post<
        ApiResponse<ExecuteReportResponse> | ExecuteReportResponse
      >(`${API_BASE_PATH}/scheduleRun/${id}`)

      logger.log('Platform report scheduled:', response)

      // Handle response that might be wrapped in data property
      const result =
        (response as ApiResponse<ExecuteReportResponse>).data || (response as ExecuteReportResponse)

      return {
        success: result.success ?? true,
        message: result.message ?? 'Report scheduled successfully'
      }
    } catch (error) {
      logger.error('Failed to schedule platform report:', error)
      throw new Error('Failed to schedule platform report. Please try again later.')
    }
  }
}
